import { useRef } from 'react';
import isEqual from 'lodash/isEqual';

/**
 * A value that can be meaningfully deep-compared via lodash `isEqual`.
 * Accepts primitives and structured values (objects, arrays).
 * Excludes `symbol` and `bigint` which don't deep-compare well.
 *
 * Uses `object` rather than `Record<string, unknown>` because TS interfaces
 * lack implicit index signatures and would not be assignable otherwise.
 */
export type DeepComparableDep = string | number | boolean | null | undefined | object;

/**
 * Memoizes a value using deep comparison of dependencies.
 * Recomputes only when deps are not deep-equal to the previous deps.
 */
export function useDeepMemo<T>(factory: () => T, deps: readonly DeepComparableDep[]): T {
  const ref = useRef<{ deps: readonly DeepComparableDep[]; value: T }>();

  if (!ref.current || !isEqual(deps, ref.current.deps)) {
    ref.current = { deps, value: factory() };
  }

  return ref.current.value;
}
